import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useVarianceAppContext } from './VarianceAppContext';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import Button from './ui/Button';
import Badge from './ui/Badge';
import { Users, Building2, TrendingUp, BarChart3, X, Calendar, Banknote, Filter, PieChart } from 'lucide-react';
import './PeerComparison.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const ModernPeerComparison = ({
  selectedBanks = [],
  selectedPeriodType = '',
  selectedCurrentPeriod = '',
  selectedCategory = '',
  selectedItems = [],
  selectedViewType = 'actual',
  financialItems = []
}) => {
  const { country } = useCountry();
  const { selectedBusinessSegment } = useVarianceAppContext();
  
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState(selectedViewType);

  // Memoize fetchComparisonData to prevent unnecessary re-creation
  const fetchComparisonData = useCallback(async () => {
    if (!selectedBusinessSegment || selectedBanks.length === 0 || !selectedCategory || !selectedCurrentPeriod || selectedItems.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch data for each selected bank
      const bankDataPromises = selectedBanks.map(async (bank) => {
        const response = await axios.get(`${config.apiUrl}/filtered-data`, {
          params: {
            business_segment: selectedBusinessSegment,
            bank: bank,
            category: selectedCategory,
            view_type: viewType,
            financial_items: selectedItems.join(',')
          },
          headers: getDefaultHeaders(country)
        });
        return { bank, data: response.data?.data || [] };
      });

      const bankDataResults = await Promise.all(bankDataPromises);
      
      console.log('Bank data results:', bankDataResults);
      console.log('Selected items:', selectedItems);
      console.log('Selected current period:', selectedCurrentPeriod);
      
      // Debug: Log the structure of the first bank's data
      if (bankDataResults.length > 0) {
        console.log('First bank data structure:', bankDataResults[0]);
        if (bankDataResults[0].data.length > 0) {
          console.log('First data item structure:', bankDataResults[0].data[0]);
          console.log('Available keys in first data item:', Object.keys(bankDataResults[0].data[0]));
        }
      }
      
      // Transform data for comparison chart
      const transformedData = selectedItems.map(itemId => {
        const item = financialItems.find(i => i.id === itemId);
        if (!item) {
          console.log(`Item not found for ID: ${itemId}`);
          return null;
        }

        const dataPoint = { item: item.Item };
        
        bankDataResults.forEach(({ bank, data }) => {
          console.log(`Processing ${bank} data:`, data);
          
          // Find the item data - try multiple matching strategies
          let itemData = null;
          
          // Strategy 1: Match by exact Item name
          itemData = data.find(d => d.Item === item.Item);
          if (itemData) {
            console.log(`Found by Item name: ${item.Item}`);
          }
          
          // Strategy 2: Match by ID
          if (!itemData) {
            itemData = data.find(d => d.id === itemId);
            if (itemData) {
              console.log(`Found by ID: ${itemId}`);
            }
          }
          
          // Strategy 3: Match by ID with different format (e.g., "1" vs "1.1")
          if (!itemData) {
            const baseId = itemId.split('.')[0];
            itemData = data.find(d => d.id === baseId);
            if (itemData) {
              console.log(`Found by base ID: ${baseId}`);
            }
          }
          
          console.log(`Final itemData for ${item.Item} in ${bank}:`, itemData);
          
          if (itemData) {
            console.log(`Looking for period key: ${selectedCurrentPeriod}`);
            console.log(`Available period keys:`, Object.keys(itemData).filter(key => 
              key.includes('q') || key.includes('h') || key.includes('fy') || key.includes('9m') || 
              key.includes('jfm') || key.includes('amj') || key.includes('jas') || key.includes('ond')
            ));
            
            // Map period format - convert q1_2025 to jfm_2025, q2_2025 to amj_2025, etc.
            let periodKey = selectedCurrentPeriod;
            if (selectedCurrentPeriod.includes('q1_')) {
              periodKey = selectedCurrentPeriod.replace('q1_', 'jfm_');
            } else if (selectedCurrentPeriod.includes('q2_')) {
              periodKey = selectedCurrentPeriod.replace('q2_', 'amj_');
            } else if (selectedCurrentPeriod.includes('q3_')) {
              periodKey = selectedCurrentPeriod.replace('q3_', 'jas_');
            } else if (selectedCurrentPeriod.includes('q4_')) {
              periodKey = selectedCurrentPeriod.replace('q4_', 'ond_');
            }
            
            console.log(`Looking for period key: ${periodKey} (original: ${selectedCurrentPeriod})`);
            
            // Check if the period key exists and has a value
            if (itemData[periodKey] !== null && itemData[periodKey] !== undefined) {
              const value = parseFloat(itemData[periodKey]) || 0;
              dataPoint[bank.toUpperCase()] = value;
              console.log(`✅ Found value for ${item.Item} in ${bank}: ${value} for period ${periodKey}`);
            } else {
              dataPoint[bank.toUpperCase()] = 0;
              console.log(`❌ No value found for ${item.Item} in ${bank} for period ${periodKey}`);
            }
          } else {
            dataPoint[bank.toUpperCase()] = 0;
            console.log(`❌ No item data found for ${item.Item} in ${bank}`);
          }
        });
        
        return dataPoint;
      }).filter(Boolean);

      console.log('Transformed comparison data:', transformedData);
      console.log('Transformed data length:', transformedData.length);
      console.log('Sample transformed data point:', transformedData[0]);
      setComparisonData(transformedData);
    } catch (err) {
      setError('Failed to fetch comparison data');
      console.error('Error fetching comparison data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessSegment, selectedBanks.join(','), selectedCategory, selectedCurrentPeriod, selectedItems.join(','), viewType, financialItems, country]);

  // Use ref to track previous selections to avoid unnecessary calls
  const prevSelectionsRef = useRef('');
  
  // Fetch comparison data when selections change
  useEffect(() => {
    const selectionsKey = `${selectedBusinessSegment}-${selectedBanks.join(',')}-${selectedCategory}-${selectedCurrentPeriod}-${selectedItems.join(',')}-${viewType}`;
    
    if (selectedBusinessSegment && selectedBanks.length > 0 && selectedCategory && selectedCurrentPeriod && selectedItems.length > 0) {
      // Only fetch if selections actually changed
      if (prevSelectionsRef.current !== selectionsKey) {
        prevSelectionsRef.current = selectionsKey;
        fetchComparisonData();
      }
    } else {
      setComparisonData([]);
      prevSelectionsRef.current = '';
    }
  }, [selectedBusinessSegment, selectedBanks.join(','), selectedCategory, selectedCurrentPeriod, selectedItems.join(','), viewType, fetchComparisonData]);

  if (!selectedBusinessSegment) {
    return (
      <Card className="peer-comparison">
        <CardHeader>
          <CardTitle>Peer Comparison Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please select a business segment to view peer comparisons.</p>
        </CardContent>
      </Card>
    );
  }

  const prepareSampleChartData = () => {
    return [
      { item: 'Revenue', ADIB: 1200000, DIB: 1500000, FAB: 1800000, ENBD: 1600000 },
      { item: 'Net Income', ADIB: 200000, DIB: 250000, FAB: 300000, ENBD: 280000 },
      { item: 'Total Assets', ADIB: 5000000, DIB: 6000000, FAB: 7000000, ENBD: 6500000 },
      { item: 'Customer Deposits', ADIB: 3000000, DIB: 3500000, FAB: 4000000, ENBD: 3800000 },
      { item: 'Loans & Advances', ADIB: 2500000, DIB: 3000000, FAB: 3500000, ENBD: 3200000 }
    ];
  };

  return (
    <>
      {/* Chart Section */}
      <div className="chart-section">
          <div className="chart-header">
            <div className="chart-title">
              <BarChart3 className="w-4 h-4" />
              <span>Peer Comparison Chart</span>
            </div>
            
            {/* View Type Toggle */}
            <div className="view-type-toggle">
              <button
                className={`view-type-button ${viewType === 'actual' ? 'active' : ''}`}
                onClick={() => setViewType('actual')}
              >
                Actual
              </button>
              <button
                className={`view-type-button ${viewType === 'qoq' ? 'active' : ''}`}
                onClick={() => setViewType('qoq')}
              >
                QoQ
              </button>
              <button
                className={`view-type-button ${viewType === 'yoy' ? 'active' : ''}`}
                onClick={() => setViewType('yoy')}
              >
                YoY
              </button>
            </div>
          </div>
          
          {/* Chart Controls */}
          <div className="chart-controls">
            <div className="chart-info">
              {selectedBanks.length > 0 && selectedItems.length > 0 && selectedCurrentPeriod ? (
                <span>Comparing {selectedBanks.length} banks across {selectedItems.length} metrics for {selectedCurrentPeriod}</span>
              ) : (
                <span>Select period, banks, and financial items above to load comparison data</span>
              )}
            </div>
            
            {selectedBanks.length > 0 && (
                <div className="selected-banks">
                  {selectedBanks.map(bank => (
                    <Badge key={bank} variant="info" className="selected-bank-badge">
                      {bank.toUpperCase()}
                    </Badge>
                  ))}
                </div>
            )}
          </div>

          {loading ? (
            <div className="chart-container">
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <span>Loading comparison data...</span>
              </div>
            </div>
          ) : error ? (
            <div className="chart-container">
              <div className="chart-error">
                <div className="chart-error-icon">
                  <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2zm0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="chart-error-title">{error}</p>
                <p className="chart-error-description">Please check your filter selections</p>
              </div>
            </div>
          ) : comparisonData.length > 0 ? (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={comparisonData} margin={{ top: 15, right: 20, left: 15, bottom: 15 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="item" 
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                    tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                  />
                  <YAxis 
                    domain={[(dataMin) => Math.max(0, dataMin * 0.9), (dataMax) => dataMax * 1.1]} 
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                    tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                    tickFormatter={(value) => {
                      const absValue = Math.abs(value);
                      if (absValue >= 1000000000) {
                        const roundedValue = Math.round(value / 100000000) / 10;
                        return `${roundedValue}B`;
                      } else if (absValue >= 1000000) {
                        const roundedValue = Math.round(value / 100000) / 10;
                        return `${roundedValue}M`;
                      } else if (absValue >= 1000) {
                        const roundedValue = Math.round(value / 100) / 10;
                        return `${roundedValue}K`;
                      } else if (absValue >= 1) {
                        return `${Math.round(value)}`;
                      } else if (absValue >= 0.01) {
                        return value.toFixed(2);
                      } else {
                        return value.toFixed(4);
                      }
                    }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value?.toLocaleString(), name]} 
                    labelFormatter={(label) => `Item: ${label}`}
                    />
                   <Legend 
                     wrapperStyle={{
                       fontSize: '10px',
                       paddingTop: '8px'
                     }}
                   />
                    {selectedBanks.map((bank, index) => {
                    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
                    const color = colors[index % colors.length];
                    
                    return (
                      <Bar
                        key={bank}
                        dataKey={bank.toUpperCase()}
                        fill={color}
                        radius={[4, 4, 0, 0]}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <>
              {/* Sample Chart for Demonstration */}
              <div className="sample-chart-header">
                <h4 className="sample-chart-title">Sample Peer Comparison Chart</h4>
                <p className="sample-chart-subtitle">Demo data for demonstration</p>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={prepareSampleChartData()} margin={{ top: 15, right: 20, left: 15, bottom: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="item" 
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                      tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                    />
                    <YAxis 
                      domain={[(dataMin) => Math.max(0, dataMin * 0.9), (dataMax) => dataMax * 1.1]} 
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                      tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                      tickFormatter={(value) => {
                        const absValue = Math.abs(value);
                        if (absValue >= 1000000000) {
                          const roundedValue = Math.round(value / 100000000) / 10;
                          return `${roundedValue}B`;
                        } else if (absValue >= 1000000) {
                          const roundedValue = Math.round(value / 100000) / 10;
                          return `${roundedValue}M`;
                        } else if (absValue >= 1000) {
                          const roundedValue = Math.round(value / 100) / 10;
                          return `${roundedValue}K`;
                        } else if (absValue >= 1) {
                          return `${Math.round(value)}`;
                        } else if (absValue >= 0.01) {
                          return value.toFixed(2);
                        } else {
                          return value.toFixed(4);
                        }
                      }}
                    />
                    <Tooltip 
                      formatter={(value, name) => [value?.toLocaleString(), name]} 
                      labelFormatter={(label) => `Item: ${label}`}
                    />
                     <Legend 
                       wrapperStyle={{
                         fontSize: '10px',
                         paddingTop: '8px'
                       }}
                     />
                      <Bar dataKey="ADIB" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="DIB" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="FAB" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="ENBD" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
      </div>
    </>
  );
};

export default ModernPeerComparison;
